import { useState } from "react";
import { Plus, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ConfigCard } from "./ConfigCard";
import {
  useStudioConfigs,
  useCreateStudioConfig,
  useDeleteStudioConfig,
  useExportBridgeConfig,
  useLoadConfigToBridge,
  useConfigureBridgeSync,
} from "@/hooks/useStudioConfigs";
import { useUpdateStudio, type Studio } from "@/hooks/useStudios";
import type { StudioConfigData } from "@production-hub/shared";

interface StudioConfigurationsProps {
  studio: Studio;
}

export const StudioConfigurations = ({ studio }: StudioConfigurationsProps) => {
  const { data: configs = [], isLoading } = useStudioConfigs(studio.id);
  const createConfig = useCreateStudioConfig();
  const deleteConfig = useDeleteStudioConfig();
  const exportBridge = useExportBridgeConfig();
  const loadToBridge = useLoadConfigToBridge();
  const configureBridgeSync = useConfigureBridgeSync();
  const updateStudio = useUpdateStudio();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [deleteConfigId, setDeleteConfigId] = useState<string | null>(null);

  const handleSaveFromBridge = async () => {
    if (!studio.bridge_url) return;
    try {
      const configData = await exportBridge.mutateAsync(studio.bridge_url);
      await createConfig.mutateAsync({
        studio_id: studio.id,
        name: `Bridge Snapshot ${new Date().toLocaleDateString()}`,
        config_data: configData,
      });
    } catch {
      // Error toasts handled by mutation hooks
    }
  };

  const handleCreate = async () => {
    if (!createName.trim()) return;
    await createConfig.mutateAsync({
      studio_id: studio.id,
      name: createName.trim(),
      description: createDescription.trim() || null,
    });
    setCreateName("");
    setCreateDescription("");
    setIsCreateOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteConfigId) return;
    // Clear active config if deleting the active one
    if (studio.active_config_id === deleteConfigId) {
      await updateStudio.mutateAsync({
        id: studio.id,
        studio: { active_config_id: null },
      });
    }
    await deleteConfig.mutateAsync({
      id: deleteConfigId,
      studio_id: studio.id,
    });
    setDeleteConfigId(null);
  };

  const handleLoadToBridge = async (config: {
    id: string;
    config_data: unknown;
  }) => {
    if (!studio.bridge_url) return;
    const configData = config.config_data as StudioConfigData;

    try {
      // Push config data to bridge
      await loadToBridge.mutateAsync({
        bridgeUrl: studio.bridge_url,
        configData,
      });

      // Configure bridge to sync back to this config
      if (studio.bridge_api_key) {
        const apiUrl = window.location.origin;
        await configureBridgeSync.mutateAsync({
          bridgeUrl: studio.bridge_url,
          apiUrl,
          configId: config.id,
          apiKey: studio.bridge_api_key,
        });
      }

      // Set as active config
      await updateStudio.mutateAsync({
        id: studio.id,
        studio: { active_config_id: config.id },
      });
    } catch {
      // Error toasts handled by mutation hooks
    }
  };

  return (
    <div className="space-y-4" data-testid="studio-configurations">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Configurations</h3>
        <div className="flex gap-2">
          {studio.bridge_url && (
            <Button
              variant="outline"
              onClick={handleSaveFromBridge}
              disabled={exportBridge.isPending || createConfig.isPending}
              data-testid="save-bridge-state-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Save Current State
            </Button>
          )}
          <Button
            onClick={() => setIsCreateOpen(true)}
            data-testid="new-config-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Config
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading...
          </CardContent>
        </Card>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <p>No configurations saved</p>
            <p className="text-sm">
              {studio.bridge_url
                ? 'Click "Save Current State" to capture bridge config'
                : "Create a new config to get started"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4" data-testid="config-list">
          {configs.map((config) => (
            <ConfigCard
              key={config.id}
              config={config}
              isActive={studio.active_config_id === config.id}
              editHref={`/studios/${studio.id}/config/${config.id}`}
              onLoadToBridge={() => handleLoadToBridge(config)}
              onDelete={() => setDeleteConfigId(config.id)}
              isLoading={loadToBridge.isPending}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent data-testid="create-config-dialog">
          <DialogHeader>
            <DialogTitle>New Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="config-name">Name</Label>
              <Input
                id="config-name"
                data-testid="config-name-input"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Production Setup"
              />
            </div>
            <div>
              <Label htmlFor="config-desc">Description (optional)</Label>
              <Textarea
                id="config-desc"
                data-testid="config-description-input"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="Main production configuration with..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!createName.trim() || createConfig.isPending}
                data-testid="create-config-submit"
              >
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteConfigId}
        onOpenChange={(open) => !open && setDeleteConfigId(null)}
      >
        <AlertDialogContent data-testid="delete-config-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Configuration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this configuration and all its
              version history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-config-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
